import { forwardRef, type PropsWithChildren, type ReactNode } from 'react';

import type {
  AriaLabelingProps,
  DataTestId,
  DOMProps,
  MaskingProps,
  StylingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import type {
  EventNotificationRequest,
  ResourceNotificationRequest,
} from '@dynatrace-sdk/client-notification-v2';

import { Content } from './components/Content.js';
import {
  EventNotificationClient,
  isEventNotificationConfig,
} from './components/EventNotificationClient.js';
import { NotificationPermissionInfo } from './components/PermissionInfo.js';
import {
  isResourceNotificationConfig,
  ResourceNotificationClient,
} from './components/ResourceNotificationClient.js';
import { NotificationToggleButton } from './components/ToggleButton.js';

/** @public */
export interface NotificationSettingsResourceNotification {
  /**
   * Defines the type of the notifcation that should be subscribed to.
   */
  type: 'resource';
  /**
   * Configuration for a resource notification. Uses the `@dynatrace-sdk/client-notification-v2`.
   */
  config: ResourceNotificationRequest;
}

/** @public */
export interface NotificationSettingsEventNotification {
  /**
   * Defines the type of the notifcation that should be subscribed to.
   */
  type: 'event';
  /**
   * Configuration for a event notification. Uses the `@dynatrace-sdk/client-notification-v2`.
   */
  config: EventNotificationRequest;
}

/** @public */
export type NotificationSettingsConfig =
  | NotificationSettingsEventNotification
  | NotificationSettingsResourceNotification;

/**
 * @public
 */
export type NotificationSettingsProps = StylingProps &
  DataTestId &
  MaskingProps &
  DOMProps &
  AriaLabelingProps &
  NotificationSettingsConfig & {
    /**
     * Either a ReactNode or a render function that gets passed certain values
     * from the currently configured notification */
    children?:
      | (({
          disabled,
          headingText,
          infoText,
          hasReadPermission,
          hasWritePermission,
        }: {
          disabled: boolean;
          headingText: string;
          infoText: string;
          hasReadPermission: boolean;
          hasWritePermission: boolean;
        }) => ReactNode)
      | ReactNode;
  };

/**
 * Switch function to determine which notification client should be used.
 * @internal
 */
export function NotificationProvider(
  props: PropsWithChildren<Pick<NotificationSettingsProps, 'type' | 'config'>>,
) {
  if (isResourceNotificationConfig(props.type, props.config)) {
    return (
      <ResourceNotificationClient config={props.config}>
        {props.children}
      </ResourceNotificationClient>
    );
  }

  if (isEventNotificationConfig(props.type, props.config)) {
    return (
      <EventNotificationClient config={props.config}>
        {props.children}
      </EventNotificationClient>
    );
  }

  return props.children;
}

/**
 * NotificationSettings component
 * @public
 */
export const NotificationSettings = forwardRef<
  HTMLDivElement,
  NotificationSettingsProps
>((props, ref) => {
  const {
    config,
    children,
    id,
    type,
    style: consumerStyle,
    className: consumerClassName,
    'data-dtrum-mask': dtRumMask,
    'data-dtrum-allow': dtRumAllow,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
    ...remainingProps
  } = props;

  return (
    <NotificationProvider type={type} config={config}>
      <Flex
        id={id}
        ref={ref}
        flexDirection="column"
        gap={12}
        style={consumerStyle}
        className={consumerClassName}
        data-dtrum-mask={dtRumMask}
        data-dtrum-allow={dtRumAllow}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        {...remainingProps}
      >
        <Content>{props.children}</Content>
        <Flex alignItems="center">
          <NotificationToggleButton />
          <NotificationPermissionInfo />
        </Flex>
      </Flex>
    </NotificationProvider>
  );
});
